import { PositionProps } from '../types';

type BoundingBox = { [key in keyof Required<PositionProps>]: number };
export type Coords = Pick<BoundingBox, 'x' | 'y'>;
type Dimensions = Pick<BoundingBox, 'h' | 'w'>;

/**
 * Calculates the center coordinates of a bounding box.
 * @param boundingBox - The bounding box to calculate the center for.
 * @returns The center coordinates of the bounding box.
 */
export const getCenter = (
  boundingBox: BoundingBox
): Pick<BoundingBox, 'x' | 'y'> => {
  return {
    x: boundingBox.x + boundingBox.w / 2,
    y: boundingBox.y + boundingBox.h / 2,
  };
};

/**
 * Centers an element around the specified coordinates.
 * @param targetCoords - The coordinates to center the element around.
 * @param dimensions - The dimensions of the element.
 * @returns The bounding box of the centered element.
 */
export const centerAround = (
  targetCoords: Coords,
  dimensions: Dimensions
): BoundingBox => {
  return {
    x: targetCoords.x - dimensions.w / 2,
    y: targetCoords.y - dimensions.h / 2,
    h: dimensions.h,
    w: dimensions.w,
  };
};

/**
 * Retrieves the coordinates of a specific point of a bounding box.
 * @param boundingBox - The bounding box to retrieve the coordinates from.
 * @param forPoint - The point to retrieve the coordinates for.
 * @returns The coordinates of the specified point.
 */
export const getBoundingBoxCoords = (
  boundingBox: BoundingBox,
  forPoint: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center'
): Coords => {
  switch (forPoint) {
    case 'top-left': {
      return { x: boundingBox.x, y: boundingBox.y };
    }
    case 'top-right': {
      return { x: boundingBox.x + boundingBox.w, y: boundingBox.y };
    }
    case 'bottom-left': {
      return { x: boundingBox.x, y: boundingBox.y + boundingBox.h };
    }
    case 'bottom-right': {
      return {
        x: boundingBox.x + boundingBox.w,
        y: boundingBox.y + boundingBox.h,
      };
    }
    case 'center': {
      return getCenter(boundingBox);
    }
  }
};

/**
 * Positions an element at the specified coordinates with the given dimensions and anchor point.
 * @param coords - The coordinates to position the element at.
 * @param dimensions - The dimensions of the element.
 * @param anchor - The anchor point to position the element relative to.
 * @returns The bounding box of the positioned element.
 */
export const positionAt = (
  coords: Coords,
  dimensions: Dimensions,
  anchor: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center'
): BoundingBox => {
  switch (anchor) {
    case 'top-left': {
      return { ...coords, ...dimensions };
    }
    case 'top-right': {
      return { x: coords.x - dimensions.w, y: coords.y, ...dimensions };
    }
    case 'bottom-left': {
      return { x: coords.x, y: coords.y - dimensions.h, ...dimensions };
    }
    case 'bottom-right': {
      return {
        x: coords.x - dimensions.w,
        y: coords.y - dimensions.h,
        ...dimensions,
      };
    }
    case 'center': {
      return centerAround(coords, dimensions);
    }
  }
};

/**
 * Retrieves the X positions of columns within a container.
 * @param containerXPosition - The X position of the container.
 * @param containerWidth - The width of the container.
 * @param columns - The number of columns.
 * @param gap - The gap between columns (default is 0).
 * @returns An object containing the column width and an array of column positions.
 */
export const getColumnXPositions = (
  containerXPosition: number,
  containerWidth: number,
  columns: number,
  gap = 0
): { columnWidth: number; positions: number[] } => {
  const columnWidth = (containerWidth - gap * (columns - 1)) / columns;
  const positions = Array.from(
    { length: columns },
    (_, i) => containerXPosition + i * (columnWidth + gap)
  );
  return { columnWidth, positions };
};
